import { APIv4 as API } from 'src/libs/axios';
import { alertError } from 'src/libs/toastify';
import { ShortShop } from 'src/pages/management/ShopsPage/ShortShopField/services';
import { NomenclatureItem } from '../types';

const Messages = {
  FETCH_TECHNICAL_CARDS: 'Ошибка получения списка технологических карт',
  FETCH_TECHNICAL_CARD: 'Ошибка получения данных технологической карты',
  CREATE_TECHNICAL_CARD: 'Ошибка создания технологической карты',
  UPDATE_TECHNICAL_CARD: 'Ошибка обновления технологической карты',
  ACTIVATE_TECHNICAL_CARD: 'Ошибка активации технологической карты',
};

// GET /technical-cards (список)
export type TechnicalCardListItem = {
  id: string;
  number: string;
  isDefault: boolean;
  type: number;
  nomenclature: NomenclatureItem | null;
  shop: ShortShop | null;
  yieldTransformNomenclature: NomenclatureItem | null;
  yieldTransformQuantity: number;
  yieldWeight: number;
  yieldQuantity: number;
};

// Элемент технической карты (payload)
export type TechnicalCardItemPayload = {
  id?: string;
  nomenclatureId: string;
  quantity: number;
  unitOfMeasurement: number;
  grossWeight: number;
  netWeight: number;
  yieldWeight: number;
  yieldColdProcessingPercent: number;
  yieldHotProcessingPercent: number;
  comment: string;
};

// клиентские расчётные и отображаемые поля
export type TechnicalCardItem = {
  id?: string;
  nomenclatureId?: string;
  nomenclature?: NomenclatureItem | null;
  unitOfMeasurement: number;
  quantity: number;
  grossWeight: number;
  netWeight: number;
  yieldWeight: number;
  yieldColdProcessingPercent: number;
  yieldHotProcessingPercent: number;
  comment: string;
  unitOfMeasurementName?: string;
  weightPerUnit?: number;
  isNew?: boolean;
  baseUnitOfMeasurement?: number;
  baseWeightPerUnit?: number;
};

// GET /technical-cards/{id}
export type TechnicalCardDetail = {
  id: string;
  number: string;
  isDefault: boolean;
  type: number;
  nomenclatureId: string;
  shopId: number | null;
  yieldTransformNomenclatureId: string | null;
  yieldTransformQuantity: number;
  yieldWeight: number;
  yieldQuantity: number;
  items: TechnicalCardItem[];
};

// POST/PUT
export type TechnicalCardPayload = {
  nomenclatureId: string;
  type: number;
  yieldQuantity: number;
  shopId: number | null;
  yieldTransformNomenclatureId: string | null;
  yieldTransformQuantity: number;
  items: TechnicalCardItemPayload[];
};

// для формы
export type TechnicalCardValues = {
  nomenclatureId: string | null;
  type: number | null;
  yieldQuantity: number | null;
  shopId: number | null;
  yieldTransformNomenclatureId: string | null;
  yieldTransformQuantity: number | null;
  items: TechnicalCardItem[];
  nomenclature?: NomenclatureItem | null;
  shop?: ShortShop | null;
  yieldTransformNomenclature?: NomenclatureItem | null;
  number?: string | null;
  isDefault?: boolean;
};

const BASE_URL = '/back-office/technical-cards';

// для отправки формы
const adaptTechnicalCard = (
  values: TechnicalCardValues
): TechnicalCardPayload => ({
  nomenclatureId: values.nomenclatureId ?? '',
  type: values.type ?? 0,
  yieldQuantity: values.yieldQuantity ?? 0,
  shopId: values.shopId ?? null,
  yieldTransformNomenclatureId: values.yieldTransformNomenclatureId,
  yieldTransformQuantity: values.yieldTransformQuantity ?? 0,
  items: adaptItems(values.items),
});

const adaptItems = (items: TechnicalCardItem[]): TechnicalCardItemPayload[] =>
  items.map((item) => ({
    ...(item.isNew ? {} : { id: item.id }),
    nomenclatureId: item.nomenclatureId ?? item.nomenclature?.id ?? '',
    quantity: item.quantity,
    unitOfMeasurement: item.unitOfMeasurement,
    grossWeight: item.grossWeight,
    netWeight: item.netWeight,
    yieldWeight: item.yieldWeight,
    yieldColdProcessingPercent: item.yieldColdProcessingPercent,
    yieldHotProcessingPercent: item.yieldHotProcessingPercent,
    comment: item.comment,
  }));

// CRUD операции
export const fetchAllTechnicalCards = (params?: {
  searchName?: string;
}): Promise<TechnicalCardListItem[] | void> =>
  API.get<TechnicalCardListItem[]>(BASE_URL, { params })
    .then(({ data }) => data)
    .catch((err) => alertError(err, Messages.FETCH_TECHNICAL_CARDS));

export const fetchTechnicalCard = ({
  id,
}: {
  id: string;
}): Promise<TechnicalCardDetail | void> =>
  API.get<TechnicalCardDetail>(`${BASE_URL}/${id}`)
    .then(({ data }) => data)
    .catch((err) => alertError(err, Messages.FETCH_TECHNICAL_CARD));

export const addTechnicalCard = (data: TechnicalCardValues): Promise<string> =>
  API.post<string>(BASE_URL, adaptTechnicalCard(data)).then(({ data }) => data);

export const updateTechnicalCard = (params: {
  id: string;
  data: TechnicalCardValues;
}): Promise<boolean> =>
  API.put(`${BASE_URL}/${params.id}`, adaptTechnicalCard(params.data)).then(
    ({ data }) => Boolean(data)
  );

export const activateTechnicalCard = ({
  id,
}: {
  id: string;
}): Promise<boolean> =>
  API.post(`${BASE_URL}/${id}/activate`).then(({ data }) => Boolean(data));
